/**
 * "Change category" — one control, any page (rd15 lane B, owner decisions 5-7).
 *
 * WHY THIS IS ITS OWN MODULE. The Expense audit page has had a category
 * chooser for the owner since the audit design; the owner now allows the
 * account manager to change a category too (decision 5), and the same control
 * belongs on a transaction's detail where a person is already looking at the
 * line (lane A leaves an empty `data-slot="change-category"` there). Building
 * it once means both surfaces obey one set of rules instead of two copies
 * drifting.
 *
 * WHAT IT DOES, AND WHAT IT REFUSES.
 *   - It changes the ONE line of a single-line booking. A split (or a paired
 *     intercompany item) needs more than a new account, so the control says so
 *     and points at Split rather than quietly rewriting part of the booking
 *     (the same rule the Expense audit page learned as R5-02/R5-05).
 *   - A correction is a NEW entry. The control always sends the whole line and
 *     the hash of the booking it was written against, so the Worker can refuse
 *     a change made against a booking that has since moved. It never edits
 *     history.
 *   - "Undo" is decision 7: change it again, or cancel a change that has not
 *     been applied yet. There is no reverse-and-requeue, and the pending block
 *     says plainly that a change already applied is changed again, not undone.
 *   - A closed month stays the owner's (decision 5). The account manager reads
 *     the ask-owner sentence instead of a control the Worker would refuse; an
 *     unknown month state fails closed the same way.
 *   - Receipt images are the item's own documents. The host mints the URLs (the
 *     Worker decides whether this reader may see them); a refusal reads as the
 *     shared failure sentence, never as a raw id or a server message.
 *
 * The host supplies every read and write, so this module holds no fetch, no
 * route and no company logic: Expense audit today, a transaction detail later.
 */
import { mountChartChooser } from "../chart_chooser.js";
import { h } from "../dom.js";
import type { AccountLabelContext } from "./display-words.js";
import { advice, mayWithContext } from "./capabilities.js";
import { failureCopy, outcomeSentence } from "./failure.js";
import * as face from "./face.js";
import { runWrite } from "./write.js";
import type { AdminClient } from "../api.js";
import type { AccountPath, CompanySlug, StaffRole } from "../types.js";

/** One booked line the control is written against. */
export interface ChangeCategoryLeg {
  account: string;
  location: string;
  amount: string;
}

export interface ChangeCategoryItem {
  company: CompanySlug;
  /** The audit target this line belongs to (`target_id`). */
  targetId: string;
  /** The booking's hash: a change is refused if the booking has moved. */
  legsSha256: string;
  /** True when the line sits in a closed month (owner-only change). */
  periodLocked: boolean;
  /** The lines booked now. One line can be changed here; more cannot. */
  legs: readonly ChangeCategoryLeg[];
  /** Receipt documents the item already names (ids only; never file text). */
  documentIds?: readonly string[];
  /** A change recorded but not applied by the nightly run yet. */
  pending?: { decisionId: string; legs: readonly ChangeCategoryLeg[] } | null;
}

export interface ChangeCategoryWrite {
  /** The whole line, with the new account and place. */
  legs: readonly ChangeCategoryLeg[];
  /** The pending change this answer replaces, when there is one. */
  replacesDecisionId?: string;
}

export interface ChangeCategoryHost {
  role: StaffRole;
  item: ChangeCategoryItem;
  /**
   * What this reader may pick from: the company's open chart and its places,
   * and the page's one account-label context (rd13 L1).
   */
  accounts: readonly AccountPath[];
  locations: readonly string[];
  labelCompanies: AccountLabelContext;
  /**
   * The console client. The module owns the three writes it makes (record,
   * cancel, mint document URLs) so every one of them runs inside `runWrite`,
   * with one flight per item and one failure wording — a host cannot wire a
   * bare client call around the primitive.
   */
  client: AdminClient;
  /** Re-read the item after a change landed (the host's own read). */
  reload?: () => void | Promise<void>;
  announce: (message: string) => void;
}

export interface ChangeCategoryHandle {
  root: HTMLElement;
}

/** Said instead of a control when the booking is not one line. */
export const MULTI_LINE_CHANGE_TEXT =
  "This item is split. Use Split to change it.";

/** Said when the item's booking lines did not arrive. */
export const NO_BOOKING_LINES_TEXT =
  "The booking lines for this item could not be read. Try again later.";

/** Said under the control: what a change is, and what it is not. */
export const CHANGE_IS_A_NEW_ENTRY_TEXT =
  "A change is a new entry. It is applied on the next run.";

/** Decision 7, in the pending block: there is no reverse-and-requeue. */
export const UNDO_PLAINLY_TEXT =
  "A change already applied is not undone. Change the category again.";

/**
 * A signed document URL carries a token, so it is only ever used over https.
 * The same rule the Expense audit page applies to the same URLs.
 */
function safeSignedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return null;
    return parsed.href;
  } catch {
    return null;
  }
}

export function mountChangeCategory(host: ChangeCategoryHost): ChangeCategoryHandle {
  const { item, role } = host;
  const root = h("div", {
    class: "change-category",
    "data-role": "change-category",
    "data-company": item.company,
  });
  const status = h("p", { class: "muted", "data-role": "change-category-status" }, []);
  const closedMonth = item.periodLocked;
  // The capability table owns WHO may act and the closed-month exception; the
  // advice module owns the words. `mayWithContext` fails closed when the month
  // state is unknown, so an unread month never offers the account manager a
  // control the Worker would refuse.
  const mayChange = mayWithContext("decide_expense_audit", role, { closedMonth });
  const mayCancel = mayWithContext("cancel_expense_audit", role, { closedMonth });

  const paint = (): void => {
    while (root.firstChild) root.removeChild(root.firstChild);
    const lines: Node[] = [
      h("p", { class: "field-label" }, ["Category"]),
    ];
    if (item.legs.length === 0) {
      // Nothing to change: the booking lines did not arrive. Say so instead of
      // offering a control that would send an empty answer.
      lines.push(
        h("p", { class: "muted", "data-role": "change-category-unavailable" }, [
          NO_BOOKING_LINES_TEXT,
        ]),
      );
      root.append(...lines, status);
      return;
    }
    if (item.legs.length > 1) {
      lines.push(
        h("p", { class: "muted", "data-role": "change-category-split" }, [
          MULTI_LINE_CHANGE_TEXT,
        ]),
      );
      root.append(...lines, status);
      return;
    }
    const line = item.legs[0];
    if (line === undefined) {
      root.append(...lines, status);
      return;
    }
    // The current answer, in plain words (never the colon path), told apart
    // from a look-alike account by the host's own list.
    lines.push(
      h("p", { "data-role": "change-category-current" }, [
        face.account(line.account, host.labelCompanies, host.accounts),
      ]),
    );
    if (item.pending !== null && item.pending !== undefined) {
      paintPending(lines);
      root.append(...lines, status);
      return;
    }
    if (!mayChange) {
      lines.push(
        h("p", { class: "muted", "data-role": "change-category-advice" }, [
          advice("decide_expense_audit", role, { closedMonth }),
        ]),
      );
      root.append(...lines, status);
      return;
    }
    paintEditor(lines, line);
    root.append(...lines, status);
  };

  /** A recorded change waiting for the run: change it again, or cancel it. */
  const paintPending = (lines: Node[]): void => {
    const pending = item.pending;
    if (pending === null || pending === undefined) return;
    lines.push(
      h("p", { "data-role": "change-category-pending" }, [
        "A change is recorded and waits for the next run.",
      ]),
    );
    if (mayCancel) {
      const cancel = h(
        "button",
        {
          type: "button",
          class: "btn btn-secondary",
          "data-action": "cancel-category-change",
          "aria-label": "Cancel the pending change",
        },
        ["Cancel the change"],
      );
      cancel.addEventListener("click", () => {
        void cancelPending(pending.decisionId);
      });
      lines.push(cancel);
    } else {
      lines.push(
        h("p", { class: "muted", "data-role": "change-category-advice" }, [
          advice("cancel_expense_audit", role, { closedMonth }),
        ]),
      );
    }
    lines.push(
      h("p", { class: "muted", "data-role": "change-category-undo-note" }, [
        UNDO_PLAINLY_TEXT,
      ]),
    );
  };

  const paintEditor = (lines: Node[], line: ChangeCategoryLeg): void => {
    const accountPick = mountChartChooser({
      kind: "account",
      selectable: host.accounts,
      className: "account-input",
      ariaLabel: "Category",
      placeholder: "Category",
      companies: host.labelCompanies,
    });
    const locationPick = mountChartChooser({
      kind: "location",
      selectable: host.locations,
      className: "account-input",
      ariaLabel: "Place",
      placeholder: "Place",
      companies: host.labelCompanies,
    });
    const save = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "change-category",
        "aria-label": "Change category",
      },
      ["Change category"],
    );
    const sync = (): void => {
      save.disabled =
        accountPick.getValue() === null || locationPick.getValue() === null;
    };
    accountPick.filter.addEventListener("change", sync);
    accountPick.filter.addEventListener("blur", sync);
    locationPick.filter.addEventListener("change", sync);
    locationPick.filter.addEventListener("blur", sync);
    sync();
    save.addEventListener("click", () => {
      const account = accountPick.getValue();
      const location = locationPick.getValue();
      if (account === null || location === null) return;
      void submit([{ account, location, amount: line.amount }]);
    });
    lines.push(
      h("label", {}, ["Category", accountPick.root]),
      h("label", {}, ["Place", locationPick.root]),
      h("p", { class: "muted", "data-role": "change-category-note" }, [
        CHANGE_IS_A_NEW_ENTRY_TEXT,
      ]),
      save,
    );
    if ((item.documentIds ?? []).length > 0) {
      lines.push(receiptImages());
    }
  };

  /**
   * The receipt images behind this line. The host mints the URLs, so the
   * Worker still decides whether this reader may see them.
   */
  const receiptImages = (): HTMLElement => {
    const wrap = h("div", { "data-role": "change-category-receipt" });
    const show = h(
      "button",
      {
        type: "button",
        class: "btn btn-ghost",
        "data-action": "show-receipt-images",
        "aria-label": "Show receipt images",
      },
      ["Show receipt images"],
    );
    const holder = h("div", { "data-role": "change-category-images" });
    const showImages = async (): Promise<void> => {
      const ids = item.documentIds ?? [];
      if (ids.length === 0) return;
      let urls: readonly { documentId: string; url: string }[] = [];
      // Minting is a POST (a signed URL carries a token), so it runs through
      // the one write primitive: one flight, the whole control disabled, and
      // one failure wording.
      const outcome = await runWrite({
        key: `change-category-images:${item.targetId}`,
        scope: root,
        gate: () => null,
        send: async () => {
          urls = await host.client.mintExpenseAuditDocumentUrls(item.company, ids);
        },
        stillCurrent: () => root.isConnected,
      });
      while (holder.firstChild) holder.removeChild(holder.firstChild);
      if (outcome.kind !== "landed") {
        const copy = failureCopy(outcome.error, { role, context: "read" });
        holder.append(h("p", { class: "muted" }, [copy.sentence]));
        return;
      }
      let shown = 0;
      for (const row of urls) {
        const href = safeSignedUrl(row.url);
        if (href === null) continue;
        holder.append(
          h("p", {}, [
            h("a", {
              href,
              target: "_blank",
              rel: "noopener noreferrer",
            }, ["Open the receipt"]),
          ]),
        );
        shown += 1;
      }
      if (shown === 0) {
        holder.append(h("p", { class: "muted" }, ["No receipt image to show."]));
      }
    };
    show.addEventListener("click", () => {
      void showImages();
    });
    wrap.append(show, holder);
    return wrap;
  };

  const submit = async (
    legs: readonly ChangeCategoryLeg[],
    replacesDecisionId?: string,
  ): Promise<void> => {
    const outcome = await runWrite({
      key: `change-category:${item.targetId}`,
      scope: root,
      gate: () => null,
      send: () =>
        host.client.postExpenseAuditDecision(item.company, item.targetId, {
          expected_legs_sha256: item.legsSha256,
          lines: legs.map((leg) => ({
            company: item.company,
            account: leg.account,
            location: leg.location,
            amount: leg.amount,
          })),
          ...(replacesDecisionId !== undefined
            ? { replaces_decision_id: replacesDecisionId }
            : {}),
        }),
      stillCurrent: () => root.isConnected,
      ...(host.reload !== undefined
        ? {
            afterLanded: async () => {
              await host.reload?.();
            },
          }
        : {}),
    });
    const words = outcomeSentence(outcome, role);
    status.textContent = words;
    host.announce(words);
    if (outcome.kind === "landed" && host.reload === undefined) paint();
  };

  const cancelPending = async (decisionId: string): Promise<void> => {
    const outcome = await runWrite({
      key: `cancel-category-change:${item.targetId}`,
      scope: root,
      gate: () => null,
      send: () => host.client.cancelExpenseAuditDecision(item.company, decisionId),
      stillCurrent: () => root.isConnected,
      ...(host.reload !== undefined
        ? {
            afterLanded: async () => {
              await host.reload?.();
            },
          }
        : {}),
    });
    const words = outcomeSentence(outcome, role);
    status.textContent = words;
    host.announce(words);
    if (outcome.kind === "landed" && host.reload === undefined) paint();
  };

  paint();
  return { root };
}

/**
 * The sentence a host says when a reader may not change this item's category at
 * all — the same advice the control renders, so a host cannot invent a second
 * wording.
 */
export function changeCategoryAdvice(
  role: StaffRole,
  closedMonth: boolean,
): string {
  return advice("decide_expense_audit", role, { closedMonth });
}

/** The same for cancelling a pending change (decision 7). */
export function cancelCategoryChangeAdvice(
  role: StaffRole,
  closedMonth: boolean,
): string {
  return advice("cancel_expense_audit", role, { closedMonth });
}
